import { useState, useEffect } from "react";
import { useAuth } from "../login/AuthContext.jsx";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Grid,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  EmojiEvents as TrophyIcon,
  Business as WorkspaceIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  AccountCircle as AccountCircleIcon
} from "@mui/icons-material";
import { toast } from "react-toastify";
import config from "../../config/config";

export default function UserProfile() {
  const { userData, token } = useAuth();

  const [userForm, setUserForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    username: "",
  });

  const [dealerInfo, setDealerInfo] = useState({
    dealerName: "",
    dealerStatus: "",
    certificate: "",
    dealerId: null,
    dealerTypes: []
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [loading, setLoading] = useState(false);
  const [dealerLoading, setDealerLoading] = useState(false);

  // Kullanıcı bilgilerini çek
  useEffect(() => {
    const fetchUserById = async () => {
      // userData'dan id'yi al (hem küçük hem büyük harf kontrol et)
      const userId = userData?.id || userData?.Id;

      if (!userId || !token) {
        console.log("userId veya token yok:", { userId, token: !!token });
        return;
      }


      try {
        const response = await fetch(`${config.baseURL}/kullanici/${userId}`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Kullanıcı bilgileri alınamadı");
        }

        const data = await response.json();

        // API response'u data.data içinde geliyor
        const userData = data.data || data;

        // Veriyi state'e set et
        const newUserForm = {
          firstName: userData.Adi || "",
          lastName: (userData.Soyadi || "").trim(), // Boşlukları temizle
          email: userData.EPosta || "",
          phone: userData.Tel || "",
          username: userData.KullaniciAdi || "",
        };

        setUserForm(newUserForm);

      } catch (error) {
        console.error("Kullanıcı bilgisi alınırken hata oluştu:", error);
        toast.error("Kullanıcı bilgileri yüklenirken hata oluştu");
      }
    };

    fetchUserById();
  }, [userData?.id, userData?.Id, token]); // Bağımlılıklar düzeltildi

  const handleUserChange = (e) => {
    const { name, value } = e.target;
    setUserForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  // Bayi bilgilerini getir
  const fetchDealerInfo = async () => {
    if (!userData?.BayiKodu) return;

    setDealerLoading(true);
    try {
      const response = await fetch(`${config.baseURL}/bayiler/${userData.BayiKodu}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        const dealer = data.data;
        setDealerInfo(prev => ({
          ...prev,
          dealerName: dealer?.BayiAdi || "",
          dealerStatus: dealer?.BayiTuru || "",
          certificate: dealer?.SertifikaNo || "",
          dealerId: dealer?.Id || null,
        }));
      }
    } catch (error) {
      console.error("Bayi bilgileri alınırken hata:", error);
    } finally {
      setDealerLoading(false);
    }
  };

  // Bayi türlerini getir
  const fetchDealerTypes = async () => {
    try {
      const response = await fetch(`${config.baseURL}/bayiler/bayi-yetki/bayi-yetki-tur`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setDealerInfo(prev => ({
          ...prev,
          dealerTypes: data.data || []
        }));
      }
    } catch (error) {
      console.error("Bayi türleri alınırken hata:", error);
    }
  };

  // Bayi bilgilerini yükle
  useEffect(() => {
    if (token && userData?.BayiKodu) {
      fetchDealerInfo();
      fetchDealerTypes();
    }
  }, [token, userData?.BayiKodu]);

  const handleSaveUser = async (e) => {
    e.preventDefault();

    if (!userForm.firstName.trim()) {
      toast.error('Ad alanı boş bırakılamaz');
      return;
    }

    if (!userForm.lastName.trim()) {
      toast.error('Soyad alanı boş bırakılamaz');
      return;
    }

    if (!userForm.email.trim()) {
      toast.error('E-posta alanı boş bırakılamaz');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userForm.email)) {
      toast.error('Geçerli bir e-posta adresi giriniz');
      return;
    }

    // Telefon opsiyonel ama doldurulmuşsa geçerlilik kontrolü
    if (userForm.phone.trim()) {
      const phoneRegex = /^[0-9]{10,11}$/;
      const cleanPhone = userForm.phone.replace(/[^\d]/g, '');
      if (!phoneRegex.test(cleanPhone)) {
        toast.error('Geçerli bir telefon numarası giriniz (10-11 rakam)');
        return;
      }
    }


    setLoading(true);
    try {
      const userId = userData?.id || userData?.Id;
      const response = await fetch(`${config.baseURL}/kullanici/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          Adi: userForm.firstName,
          Soyadi: userForm.lastName,
          EPosta: userForm.email,
          Tel: userForm.phone,
          KullaniciAdi: userForm.username,
          BayiId: userData?.BayiKodu  || null
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Kullanıcı bilgileri başarıyla güncellendi');
      } else {
        toast.error(data.message || 'Kullanıcı bilgileri güncellenirken hata oluştu');
      }
    } catch (error) {
      console.error("Kullanıcı güncelleme hatası:", error);
      toast.error('Kullanıcı bilgileri güncellenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePassword = async (e) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("Yeni şifre ve doğrulama eşleşmiyor");
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }

    setLoading(true);
    try {
      const userId = userData?.id || userData?.Id;
      const response = await fetch(`${config.baseURL}/kullanici/sifre/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          MevcutSifre: passwordForm.currentPassword,
          YeniSifre: passwordForm.newPassword
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Şifre başarıyla güncellendi');
        setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      } else {
        toast.error(data.message || 'Şifre güncellenirken hata oluştu');
      }
    } catch (error) {
      console.error("Şifre güncelleme hatası:", error);
      toast.error('Şifre güncellenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Statüye göre renk ve ikon belirleme
  const getStatusConfig = (status) => {
    switch(status) {
      case "Altın":
        return { color: "#aa9b01", icon: "🥇" };
      case "Gümüş":
        return { color: "#9ca3af", icon: "🥈" };
      case "Bronz":
        return { color: "#92400e", icon: "🥉" };
      default:
        return { color: "#00C08B", icon: "⭐" };
    }
  };

  const statusConfig = getStatusConfig(dealerInfo.dealerStatus);

  // Debug için
  return (
    <Box sx={{ p: 4, maxWidth: "100%", margin: "0 auto" }}>

      {/* Bayi Bilgileri - Üstte, 100% genişlik */}
      <Card
        sx={{
          mb: 4,
          background: "#1C355E",
          color: "white",
          boxShadow: "0 4px 12px rgba(28, 53, 94, 0.15)",
          borderRadius:"16px"
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              fontWeight: 600,
              opacity: 0.95
            }}
          >
            Bayi Bilgileri
          </Typography>

          {dealerLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress sx={{ color: "white" }} />
            </Box>
          ) : (
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    opacity: 0.75,
                    mb: 1,
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px"
                  }}
                >
                  Bayi Adı
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 1
                  }}
                >
                  <WorkspaceIcon sx={{ fontSize: 24 }} />
                  {dealerInfo.dealerName || "Bayi bilgisi bulunamadı"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    opacity: 0.75,
                    mb: 1,
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px"
                  }}
                >
                  Bayi Statüsü
                </Typography>
                {dealerInfo.dealerStatus ? (
                  <Chip
                    icon={<TrophyIcon sx={{ fontSize: 20, color: "white !important" }} />}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <span>{statusConfig.icon}</span>
                        <span style={{ fontWeight: 600 }}>{dealerInfo.dealerStatus}</span>
                      </Box>
                    }
                    sx={{
                      background: statusConfig.color,
                      color: "white",
                      fontSize: "16px",
                      height: "40px",
                      px: 1,
                      "& .MuiChip-icon": {
                        color: "white"
                      }
                    }}
                  />
                ) : (
                  <Typography variant="h6" sx={{ fontWeight: 600, opacity: 0.7 }}>
                    Statü bilgisi yok
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    opacity: 0.75,
                    mb: 1,
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px"
                  }}
                >
                  Sertifika No
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontFamily: "monospace",
                    letterSpacing: "1px"
                  }}
                >
                  {dealerInfo.certificate || "Sertifika bilgisi yok"}
                </Typography>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={4}>
        {/* Kullanıcı Bilgileri */}
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.04)", borderRadius:"16px" }}>
            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  color: "#1C355E"
                }}
              >
                Kullanıcı Bilgileri
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                {/* Ad ve Soyad yan yana */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "#1C355E", fontWeight: 500 }}>
                      Ad
                    </Typography>
                     <TextField
                       fullWidth
                       placeholder="Ad"
                       name="firstName"
                       value={userForm.firstName}
                       onChange={handleUserChange}
                       variant="outlined"
                       InputProps={{
                         startAdornment: (
                           <PersonIcon sx={{ mr: 1, color: "action.active", fontSize: 20 }} />
                         ),
                       }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#f9fafb",
                          "&:hover fieldset": {
                            borderColor: "#d1d5db",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "white",
                            "& fieldset": {
                              borderColor: "#9ca3af",
                            }
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "#1C355E", fontWeight: 500 }}>
                      Soyad
                    </Typography>
                     <TextField
                       fullWidth
                       placeholder="Soyad"
                       name="lastName"
                       value={userForm.lastName}
                       onChange={handleUserChange}
                       variant="outlined"
                       InputProps={{
                         startAdornment: (
                           <PersonIcon sx={{ mr: 1, color: "action.active", fontSize: 20 }} />
                         ),
                       }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#f9fafb",
                          "&:hover fieldset": {
                            borderColor: "#d1d5db",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "white",
                            "& fieldset": {
                              borderColor: "#9ca3af",
                            }
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>

                <Box>
                  <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "#1C355E", fontWeight: 500 }}>
                    Kullanıcı Adı
                  </Typography>
                   <TextField
                     fullWidth
                     placeholder="Kullanıcı Adı"
                     name="username"
                     value={userForm.username}
                     onChange={handleUserChange}
                     variant="outlined"
                     InputProps={{
                       startAdornment: (
                         <AccountCircleIcon sx={{ mr: 1, color: "action.active", fontSize: 20 }} />
                       ),
                     }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f9fafb",
                        "&:hover fieldset": {
                          borderColor: "#d1d5db",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "white",
                          "& fieldset": {
                            borderColor: "#9ca3af",
                          }
                        }
                      }
                    }}
                  />
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "#1C355E", fontWeight: 500 }}>
                    E-posta Adresi
                  </Typography>
                   <TextField
                     fullWidth
                     placeholder="E-posta adresinizi girin"
                     name="email"
                     type="email"
                     value={userForm.email}
                     onChange={handleUserChange}
                     variant="outlined"
                     InputProps={{
                       startAdornment: (
                         <EmailIcon sx={{ mr: 1, color: "action.active", fontSize: 20 }} />
                       ),
                     }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f9fafb",
                        "&:hover fieldset": {
                          borderColor: "#d1d5db",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "white",
                          "& fieldset": {
                            borderColor: "#9ca3af",
                          }
                        }
                      }
                    }}
                  />
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "#1C355E", fontWeight: 500 }}>
                    Telefon Numarası
                  </Typography>
                   <TextField
                     fullWidth
                     placeholder="0-000-0000-0000"
                     name="phone"
                     value={userForm.phone}
                     onChange={handleUserChange}
                     variant="outlined"
                     InputProps={{
                       startAdornment: (
                         <PhoneIcon sx={{ mr: 1, color: "action.active", fontSize: 20 }} />
                       ),
                     }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f9fafb",
                        "&:hover fieldset": {
                          borderColor: "#d1d5db",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "white",
                          "& fieldset": {
                            borderColor: "#9ca3af",
                          }
                        }
                      }
                    }}
                  />
                </Box>

                <Button
                  variant="contained"
                  onClick={handleSaveUser}
                  disabled={loading}
                  sx={{
                    mt: 1,
                    py: 1.5,
                    background: "rgb(0, 167, 181)",
                    fontWeight: 600,
                    "&:hover": {
                      background: "rgb(0, 147, 161)",
                    }
                  }}
                >
                  {loading ? <CircularProgress size={20} color="inherit" /> : "Bilgileri Kaydet"}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Şifre Değiştir */}
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.04)", borderRadius:"16px" }}>
            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  color: "#1C355E"
                }}
              >
                Şifre Değiştir
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <Box>
                  <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "#1C355E", fontWeight: 500 }}>
                    Mevcut Şifre
                  </Typography>
                   <TextField
                     fullWidth
                     placeholder="Mevcut şifrenizi girin"
                     name="currentPassword"
                     type="password"
                     value={passwordForm.currentPassword}
                     onChange={handlePasswordChange}
                     variant="outlined"
                     InputProps={{
                       startAdornment: (
                         <LockIcon sx={{ mr: 1, color: "action.active", fontSize: 20 }} />
                       ),
                     }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f9fafb",
                        "&:hover fieldset": {
                          borderColor: "#d1d5db",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "white",
                          "& fieldset": {
                            borderColor: "#9ca3af",
                          }
                        }
                      }
                    }}
                  />
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "#1C355E", fontWeight: 500 }}>
                    Yeni Şifre
                  </Typography>
                   <TextField
                     fullWidth
                     placeholder="Yeni şifrenizi girin"
                     name="newPassword"
                     type="password"
                     value={passwordForm.newPassword}
                     onChange={handlePasswordChange}
                     variant="outlined"
                     InputProps={{
                       startAdornment: (
                         <LockIcon sx={{ mr: 1, color: "action.active", fontSize: 20 }} />
                       ),
                     }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f9fafb",
                        "&:hover fieldset": {
                          borderColor: "#d1d5db",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "white",
                          "& fieldset": {
                            borderColor: "#9ca3af",
                          }
                        }
                      }
                    }}
                  />
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ display: "block", mb: 0.5, color: "#1C355E", fontWeight: 500 }}>
                    Yeni Şifre (Tekrar)
                  </Typography>
                   <TextField
                     fullWidth
                     placeholder="Yeni şifrenizi tekrar girin"
                     name="confirmPassword"
                     type="password"
                     value={passwordForm.confirmPassword}
                     onChange={handlePasswordChange}
                     variant="outlined"
                     InputProps={{
                       startAdornment: (
                         <LockIcon sx={{ mr: 1, color: "action.active", fontSize: 20 }} />
                       ),
                     }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f9fafb",
                        "&:hover fieldset": {
                          borderColor: "#d1d5db",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "white",
                          "& fieldset": {
                            borderColor: "#9ca3af",
                          }
                        }
                      }
                    }}
                  />
                </Box>

                <Button
                  variant="contained"
                  onClick={handleSavePassword}
                  disabled={loading}
                  sx={{
                    mt: 1,
                    py: 1.5,
                    background: "rgb(0, 167, 181)",
                    fontWeight: 600,
                    "&:hover": {
                      background: "rgb(0, 147, 161)",
                    }
                  }}
                >
                  {loading ? <CircularProgress size={20} color="inherit" /> : "Şifreyi Güncelle"}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}